import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import client from "../../API";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import { globalStyles } from "../../Utils/Styles";
import { StyledDivider as Divider } from "../../Components/LightDivider";

import {
  Toolbar,
  Button,
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Stack,
  Fade,
  Grow,
  Backdrop,
  CircularProgress,

} from "@mui/material";

import {
  UploadFile as UploadFileIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as CircleIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  FolderOpen as FolderOpenIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";

const SEGMENTS = ["All", "Required", "Remaining", "Completed"];

const PublicOrganizerPage = () => {
  // URL params
  const organizerId = window.location.pathname.split("/").pop();
  const token = new URLSearchParams(window.location.search).get("token");

  // State
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [segment, setSegment] = useState("All");

  const [pendingFiles, setPendingFiles] = useState([]);
  const [assignItemId, setAssignItemId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fileInputRef = useRef(null);

  // ---------- Fetch ----------
  useEffect(() => {
    if (!organizerId || !token) {
      setError("Missing organizer ID or access token");
      setLoading(false);
      return;
    }
    fetchOrganizerData();
  }, [organizerId, token]);

  const fetchOrganizerData = async () => {
    try {
      setLoading((prev) => (organizer ? prev : true));
      setFetching(true);
      const data = await client.files.organizers.getPublicOrganizer(organizerId, token);
      setOrganizer(data);
      setError(null);
    } catch (err) {
      if (err?.response?.status === 404) setError("Organizer not found or access denied");
      else if (err?.response?.status === 401) setError("Invalid access token");
      else setError("Failed to fetch organizer");
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  // ---------- Stats (fallback if backend doesn't provide) ----------
  const stats = useMemo(() => {
    const items = organizer?.checklist_items || [];
    const total = items.length;
    const completed = items.filter((i) => i.completed).length;
    const requiredTotal = items.filter((i) => i.required).length;
    const requiredCompleted = items.filter((i) => i.required && i.completed).length;
    const pct = total ? Math.round((completed / total) * 100) : 0;
    const requiredPct = requiredTotal ? Math.round((requiredCompleted / requiredTotal) * 100) : 0;

    const s = organizer?.checklist_stats || {};
    return {
      total_items: s.total_items ?? total,
      completed_items: s.completed_items ?? completed,
      required_items: s.required_items ?? requiredTotal,
      completed_required: s.completed_required ?? requiredCompleted,
      completion_percentage: s.completion_percentage ?? pct,
      required_completion_percentage: s.required_completion_percentage ?? requiredPct,
    };
  }, [organizer]);

  // ---------- Filtering ----------
  const filteredChecklist = useMemo(() => {
    const q = query.trim().toLowerCase();
    let items = organizer?.checklist_items || [];
    if (q) {
      items = items.filter((i) => {
        const hay = `${i.title || ""} ${i.description || ""} ${(i.notes || "")} ${(i.expected_file_types || []).join(" ")}`
          .toLowerCase();
        return hay.includes(q);
      });
    }
    switch (segment) {
      case "Required":
        items = items.filter((i) => i.required);
        break;
      case "Remaining":
        items = items.filter((i) => !i.completed);
        break;
      case "Completed":
        items = items.filter((i) => i.completed);
        break;
      default:
        break;
    }
    // sort: required first, then incomplete, then title
    items.sort((a, b) => {
      const reqDiff = (b.required ? 1 : 0) - (a.required ? 1 : 0);
      if (reqDiff !== 0) return reqDiff;
      const compDiff = (a.completed ? 1 : 0) - (b.completed ? 1 : 0);
      if (compDiff !== 0) return compDiff;
      return (a.title || "").localeCompare(b.title || "");
    });
    return items;
  }, [organizer, query, segment]);

  // ---------- Snackbar ----------
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // ---------- Upload helpers ----------
  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
    });

  const getFileType = (filename) => {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    if (ext === "pdf") return "pdf";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["doc", "docx"].includes(ext)) return "document";
    return "file";
  };

  const renderFileIcon = (filename, size = "small") => {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    const iconProps = { fontSize: size, sx: { color: "text.secondary" } };
    if (ext === "pdf") return <PdfIcon {...iconProps} />;
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <ImageIcon {...iconProps} />;
    if (["doc", "docx"].includes(ext)) return <DocIcon {...iconProps} />;
    return <FileIcon {...iconProps} />;
  };

  const handlePickClick = (checklistId = null) => {
    setAssignItemId(checklistId);
    fileInputRef.current?.click();
  };

  const onInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles(files);
  };

  const resetUpload = () => {
    setPendingFiles([]);
    setAssignItemId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    try {
      for (const file of pendingFiles) {
        const base64 = await convertFileToBase64(file);
        const base64Data = (base64 || "").split(",")[1] || "";
        const uploadData = {
          file: {
            name: file.name,
            content: base64Data,
            type: file.type,
            file_type: getFileType(file.name),
          },
          checklist_item_id: assignItemId || null,
        };
        await client.files.organizers.uploadToPublicOrganizer(organizerId, token, uploadData);
      }
      showSnackbar(
        `Successfully uploaded ${pendingFiles.length} file${pendingFiles.length > 1 ? "s" : ""}! 🎉`,
        "success"
      );
      resetUpload();
      await fetchOrganizerData();
    } catch (e) {
      showSnackbar(`Upload failed: ${e?.message || "Unknown error"}`, "error");
    } finally {
      setUploading(false);
    }
  };

  // ---------- Drag & drop inside dialog ----------
  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setPendingFiles((prev) => prev.concat(files));
  };

  // ---------- Colors ----------
  const progressColor = (pct) => {
    if (pct >= 100) return "success";
    if (pct >= 70) return "info";
    if (pct >= 40) return "primary";
    return "warning";
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000" }}>
        <HomeHeader />
        <Toolbar />
        <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Fade in={loading}>
            <Stack spacing={3} alignItems="center">
              <Box sx={{ position: "relative" }}>
                <CircularProgress size={60} thickness={4} />
                <FolderOpenIcon 
                  sx={{ 
                    position: "absolute", 
                    top: "50%", 
                    left: "50%", 
                    transform: "translate(-50%, -50%)",
                    fontSize: 24,
                    color: "primary.main"
                  }} 
                />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                Loading your organizer...
              </Typography>
            </Stack>
          </Fade>
        </Box>
        <HomeFooter />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000" }}>
        <HomeHeader />
        <Toolbar />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Grow in={!!error}>
            <Card elevation={3} sx={{ textAlign: "center", p: 4 }}>
              <InfoIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                Access Error
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {error}
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<RefreshIcon />} 
                onClick={fetchOrganizerData}
                sx={{ borderRadius: 2 }}
              >
                Try Again
              </Button>
            </Card>
          </Grow>
        </Container>
        <HomeFooter />
      </Box>
    );
  }

  if (!organizer) {
    return (
      <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000" }}>
        <HomeHeader />
        <Toolbar />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="warning" icon={<InfoIcon />} sx={{ fontSize: "1.1rem", py: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Organizer not found</Typography>
            <Typography>Please check the link or contact the organizer owner.</Typography>
          </Alert>
        </Container>
        <HomeFooter />
      </Box>
    );
  }

  const shareUrl = window.location.href;

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", width: "100%", color: "#000", minHeight: "100vh" }}>
      <HomeHeader />
      <Toolbar />
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Hero Section */}
        <Fade in timeout={800}>
          <Card 
            elevation={4} 
            sx={{ 
              mb: 4, 
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              overflow: "hidden",
              position: "relative"
            }}
          >
            <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="overline" sx={{ opacity: 0.9, fontWeight: 600 }}>
                        Document Organizer
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {organizer.name}
                      </Typography>
                      {organizer.description && (
                        <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                          {organizer.description}
                        </Typography>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={(organizer.organizer_type || "general").replace("_", " ").toUpperCase()}
                        sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600 }}
                      />
                      <Chip
                        label={`${organizer.file_count || organizer.files?.length || 0} files uploaded`}
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }}
                      />
                      {organizer.created_at && (
                        <Chip
                          label={`Created ${new Date(organizer.created_at).toLocaleDateString()}`}
                          sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white" }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Stack spacing={2} alignItems="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h2" sx={{ fontWeight: 700, mb: 0 }}>
                        {stats.completion_percentage}%
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        Complete
                      </Typography>
                    </Box>
                    
                    <Stack direction="row" spacing={2}>
                      <Tooltip title="Copy link to share">
                        <Button
                          variant="outlined"
                          startIcon={<CopyIcon />}
                          onClick={() => {
                            navigator.clipboard.writeText(shareUrl);
                            showSnackbar("Link copied to clipboard! 📋", "success");
                          }}
                          sx={{ 
                            borderColor: "rgba(255,255,255,0.3)", 
                            color: "white",
                            "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" }
                          }}
                        >
                          Share
                        </Button>
                      </Tooltip>
                      
                      <Tooltip title="Refresh data">
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={fetchOrganizerData}
                          disabled={fetching}
                          sx={{ 
                            borderColor: "rgba(255,255,255,0.3)", 
                            color: "white",
                            "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" }
                          }}
                        >
                          Refresh
                        </Button>
                      </Tooltip>

                      {organizer.public_upload_enabled && (
                        <Button
                          variant="contained"
                          startIcon={<UploadFileIcon />}
                          onClick={() => handlePickClick(null)}
                          sx={{ 
                            bgcolor: "white", 
                            color: "primary.main",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" }
                          }}
                        >
                          Upload Files
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
            
            {/* Decorative elements */}
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.1)",
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 120,
                height: 120,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.05)",
                zIndex: 0,
              }}
            />
          </Card>
        </Fade>

        {/* Progress Stats */}
        {(stats.total_items > 0 || stats.completed_items > 0) && (
          <Grow in timeout={1000}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={8}>
                <Card elevation={2} sx={{ p: 3, height: "100%" }}>
                  <Stack spacing={3}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <TrendingUpIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Progress Overview
                        </Typography>
                      </Stack>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Overall Progress — {stats.completion_percentage}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={stats.completion_percentage}
                        color={progressColor(stats.completion_percentage)}
                        sx={{ 
                          height: 12, 
                          borderRadius: 6,
                          mb: 2,
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 6,
                          }
                        }}
                      />

                      {stats.required_items > 0 && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Required Items — {stats.required_completion_percentage}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={stats.required_completion_percentage}
                            color="secondary"
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                              }
                            }}
                          />
                        </>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip 
                        icon={<CheckCircleIcon />}
                        color="success" 
                        label={`${stats.completed_items} Completed`} 
                        variant="outlined"
                      />
                      <Chip
                        icon={<CircleIcon />}
                        color="warning"
                        label={`${Math.max(stats.total_items - stats.completed_items, 0)} Remaining`}
                        variant="outlined"
                      />
                      {stats.required_items > 0 && (
                        <Chip
                          icon={<AssignmentIcon />}
                          color="error"
                          label={`${Math.max(stats.required_items - (stats.completed_required || 0), 0)} Required Left`}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card elevation={2} sx={{ p: 3, height: "100%", textAlign: "center" }}>
                  <Stack spacing={2} height="100%" justifyContent="center">
                    <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "primary.main", mx: "auto" }} />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stats.completed_items}
                    </Typography>
                    <Typography color="text.secondary">
                      of {stats.total_items} items complete
                    </Typography>
                    {stats.completion_percentage === 100 && (
                      <Chip 
                        label="🎉 All Done!" 
                        color="success" 
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Grow>
        )}

        <Grid container spacing={4}>
          {/* Checklist Section */}
          <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ mb: 2 }}>
              {/* Search and Filters */}
              <Box sx={{ p: 3, pb: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    📋 Checklist Items
                  </Typography>
                  
                  <TextField
                    size="small"
                    placeholder="Search items..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ minWidth: 250 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>

              <Tabs
                value={segment}
                onChange={(_, v) => setSegment(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2 }}
              >
                {SEGMENTS.map((s) => (
                  <Tab key={s} value={s} label={s} sx={{ fontWeight: 600 }} />
                ))}
              </Tabs>
              <Divider />

              {organizer.public_upload_enabled && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CloudUploadIcon />
                  <Typography variant="body2">
                    <strong>Upload enabled:</strong> You can drag & drop files or{" "}
                    <Button 
                      onClick={() => handlePickClick(null)} 
                      color="inherit" 
                      sx={{ textDecoration: "underline", p: 0, minWidth: "auto" }}
                    >
                      browse files
                    </Button>
                    {" "}to submit documents.
                  </Typography>
                </Box>
              )}
            </Card>

            {/* Checklist Items */}
            {filteredChecklist.length > 0 ? (
              <Stack spacing={2}>
                {filteredChecklist.map((item, index) => {
                  const borderColor = item.completed
                    ? "success.main"
                    : item.required
                    ? "warning.main"
                    : "grey.300";
                  const bgColor = item.completed 
                    ? "success.50" 
                    : item.required 
                    ? "warning.50" 
                    : "background.paper";

                  return (
                    <Grow in timeout={300 + index * 100} key={item.id}>
                      <Card
                        elevation={item.completed ? 1 : 2}
                        sx={{
                          border: 2,
                          borderColor,
                          bgcolor: bgColor,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 4,
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Grid container spacing={2} alignItems="flex-start">
                            <Grid item>
                              {item.completed ? (
                                <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                              ) : (
                                <CircleIcon sx={{ color: "text.disabled", fontSize: 28 }} />
                              )}
                            </Grid>
                            
                            <Grid item xs>
                              <Stack spacing={2}>
                                <Box>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                      {item.title}
                                    </Typography>
                                    {item.required && (
                                      <Chip 
                                        size="small" 
                                        color="warning" 
                                        label="Required" 
                                        sx={{ fontWeight: 600 }}
                                      />
                                    )}
                                    {item.completed && (
                                      <Chip 
                                        size="small" 
                                        color="success" 
                                        label="Completed" 
                                        sx={{ fontWeight: 600 }}
                                      />
                                    )}
                                  </Stack>

                                  {item.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {item.description}
                                    </Typography>
                                  )}
                                </Box>

                                {item.expected_file_types?.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                      <strong>Expected file types:</strong>
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {item.expected_file_types.map((type) => (
                                        <Chip 
                                          key={type} 
                                          label={type.toUpperCase()} 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ fontSize: "0.7rem", height: 20 }}
                                        />
                                      ))}
                                    </Stack>
                                  </Box>
                                )}

                                {item.notes && (
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 2, 
                                      bgcolor: "background.default",
                                      borderLeftWidth: 4,
                                      borderLeftColor: "info.main"
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>📝 Notes:</strong> {item.notes}
                                    </Typography>
                                  </Paper>
                                )}

                                {item.completed && item.file && (
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 2, 
                                      bgcolor: "success.50",
                                      borderLeftWidth: 4,
                                      borderLeftColor: "success.main"
                                    }}
                                  >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      {renderFileIcon(item.file.filename, "small")}
                                      <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600 }}>
                                        ✅ Completed with: {item.file.filename}
                                        {item.completed_at && ` • ${new Date(item.completed_at).toLocaleDateString()}`}
                                      </Typography>
                                    </Stack>
                                  </Paper>
                                )}
                              </Stack>
                            </Grid>

                            {!item.completed && organizer.public_upload_enabled && (
                              <Grid item>
                                <Button
                                  variant="contained"
                                  size="large"
                                  startIcon={<UploadFileIcon />}
                                  onClick={() => handlePickClick(item.id)}
                                  sx={{ 
                                    borderRadius: 2,
                                    px: 3,
                                    fontWeight: 600,
                                    boxShadow: 2,
                                    "&:hover": { boxShadow: 4 }
                                  }}
                                >
                                  Upload
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grow>
                  );
                })}
              </Stack>
            ) : (
              <Card elevation={1} sx={{ p: 6, textAlign: "center" }}>
                <AssignmentIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {query || segment !== "All" ? "No items match your filters" : "No checklist items yet"}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {query || segment !== "All"
                    ? "Try adjusting your search or filter criteria."
                    : "The organizer owner hasn't added any checklist items yet."}
                </Typography>
                {(query || segment !== "All") && (
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => { setQuery(""); setSegment("All"); }}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Card>
            )}
          </Grid>

          {/* Files Sidebar */}
          <Grid item xs={12} lg={4}>
            <Card
              elevation={2}
              sx={{
                position: { lg: "sticky" },
                top: { lg: 100 },
                maxHeight: { lg: "80vh" },
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Box sx={{ p: 3, pb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    📁 Uploaded Files
                  </Typography>
                  <Chip
                    label={String(organizer.files?.length || 0)}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Box>
              
              <Divider />

              <Box sx={{ flex: 1, overflow: "auto" }}>
                {organizer.files?.length ? (
                  <List dense>
                    {organizer.files.map((file, index) => (
                      <Grow in timeout={200 + index * 50} key={file.id}>
                        <ListItem
                          sx={{
                            borderRadius: 1,
                            mx: 1,
                            mb: 0.5,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": { 
                              bgcolor: "action.hover",
                              transform: "translateX(4px)"
                            },
                          }}
                          secondaryAction={
                            file.url ? (
                              <Tooltip title="View file">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={file.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  sx={{ borderRadius: 1.5 }}
                                >
                                  View
                                </Button>
                              </Tooltip>
                            ) : null
                          }
                        >
                          <ListItemAvatar>
                            <Avatar 
                              variant="rounded" 
                              sx={{ 
                                bgcolor: "primary.50",
                                border: 1,
                                borderColor: "primary.200"
                              }}
                            >
                              {renderFileIcon(file.filename)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {file.filename}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {file.created_at ? new Date(file.created_at).toLocaleDateString() : ""}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </Grow>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <FileIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No files yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Files will appear here once uploaded
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <HomeFooter />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={onInputChange}
        style={{ display: "none" }}
        accept="*/*"
        id="public-organizer-file-input"
      />

      {/* Upload Dialog */}
      <Dialog
        open={pendingFiles.length > 0}
        onClose={() => (!uploading ? resetUpload() : null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Upload Files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select files to upload to this organizer
              </Typography>
            </Box>
            {!uploading && (
              <Button
                onClick={resetUpload}
                sx={{ minWidth: "auto", p: 1 }}
              >
                <CloseIcon />
              </Button>
            )}
          </Stack>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Assignment Selection */}
          {(organizer.checklist_items?.length || 0) > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Assign to checklist item (optional)
              </Typography>
              <TextField
                select
                fullWidth
                value={assignItemId || ""}
                onChange={(e) => setAssignItemId(e.target.value || null)}
                size="small"
                SelectProps={{ native: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                <option value="">— No assignment —</option>
                {organizer.checklist_items
                  .slice()
                  .sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0))
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.title} {i.required ? "• Required" : ""}
                      {i.completed ? " • Completed" : ""}
                    </option>
                  ))}
              </TextField>
            </Box>
          )}

          {/* Drag & Drop Zone */}
          <Box
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            sx={{
              border: "3px dashed",
              borderColor: dragActive ? "primary.main" : "grey.300",
              bgcolor: dragActive ? "primary.50" : "grey.50",
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              transition: "all 0.2s ease-in-out",
              mb: 3,
              cursor: "pointer",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "primary.50"
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Drag & drop files here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or{" "}
              <Button 
                variant="text" 
                sx={{ textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                browse files
              </Button>
              {" "}from your computer
            </Typography>
          </Box>

          {/* Files List */}
          {pendingFiles.length > 0 && (
            <Stack spacing={1} sx={{ maxHeight: 300, overflow: "auto" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Files to upload ({pendingFiles.length})
              </Typography>
              {pendingFiles.map((file, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar variant="rounded" sx={{ bgcolor: "primary.50", width: 40, height: 40 }}>
                      {renderFileIcon(file.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body1" noWrap sx={{ fontWeight: 600 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type || 'Unknown type'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => (!uploading ? resetUpload() : null)} 
            disabled={uploading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            size="large"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
            disabled={uploading || !pendingFiles.length}
            sx={{ 
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              minWidth: 140
            }}
          >
            {uploading ? "Uploading..." : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: 9999 }}
        open={uploading}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Uploading files...</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Please wait while we process your files
          </Typography>
        </Stack>
      </Backdrop>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: "100%",
            borderRadius: 2,
            boxShadow: 4
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicOrganizerPage;